import React from 'react';
import styled from '@emotion/styled';
import {
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UIBlock, PageBlock } from '../../api/uiBlocksApi';

const CanvasContainer = styled.div`
  flex: 1;
  background: #ffffff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const CanvasHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e1e5e9;
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const CanvasTitle = styled.input`
  margin: 0;
  color: #1f2937;
  font-size: 20px;
  font-weight: 600;
  border: none;
  background: transparent;
  outline: none;
  min-width: 200px;
  
  &:focus {
    background: #f9fafb;
    border-radius: 4px;
    padding: 4px 8px;
  }
`;

const CanvasActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-size: 14px;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border: 1px solid #d1d5db;
          &:hover {
            background: #f9fafb;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const CanvasContent = styled.div`
  flex: 1;
  position: relative;
  background: #f8f9fa;
`;

const DroppableArea = styled.div<{ isOver: boolean; isEmpty: boolean }>`
  min-height: ${props => props.isEmpty ? '80vh' : 'auto'};
  padding: 24px;
  background: ${props => props.isOver ? 'rgba(59, 130, 246, 0.08)' : 'transparent'};
  border: ${props => props.isOver ? '2px dashed #3b82f6' : '2px dashed rgba(229, 231, 235, 0.5)'};
  border-radius: 12px;
  margin: 16px;
  transition: all 0.3s ease;
  position: relative;
  width: calc(100% - 32px);

  ${props => props.isEmpty && `
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    color: #6b7280;
  `}

  ${props => props.isOver && `
    transform: scale(1.01);
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.15);
  `}
`;

const EmptyStateContent = styled.div`
  max-width: 400px;

  h3 {
    margin: 0 0 8px 0;
    color: #374151;
    font-size: 18px;
  }

  p {
    margin: 0 0 16px 0;
    line-height: 1.5;
  }
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const BlockContainer = styled.div<{ isDragging: boolean }>`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 16px;
  position: relative;
  transition: all 0.2s;
  opacity: ${props => props.isDragging ? 0.5 : 1};

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
  }

  &:hover .block-controls {
    opacity: 1;
  }
`;

const BlockControls = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;
`;

const ControlButton = styled.button<{ variant?: 'edit' | 'delete' | 'drag' }>`
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;

  ${props => {
    switch (props.variant) {
      case 'edit':
        return `
          background: #3b82f6;
          color: white;
          &:hover { background: #2563eb; }
        `;
      case 'delete':
        return `
          background: #ef4444;
          color: white;
          &:hover { background: #dc2626; }
        `;
      case 'drag':
        return `
          background: #6b7280;
          color: white;
          cursor: grab;
          &:hover { background: #4b5563; }
          &:active { cursor: grabbing; }
        `;
      default:
        return `
          background: #f3f4f6;
          color: #374151;
          &:hover { background: #e5e7eb; }
        `;
    }
  }}
`;

const BlockContent = styled.div`
  padding: 16px;
  
  .block-preview {
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    padding: 16px;
    background: #f9fafb;
    font-family: monospace;
    font-size: 12px;
    color: #6b7280;
    max-height: 200px;
    overflow-y: auto;
  }
`;

const BlockHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const BlockTitle = styled.h4`
  margin: 0;
  color: #1f2937;
  font-size: 14px;
  font-weight: 600;
`;

const BlockCategory = styled.span`
  background: #f3f4f6;
  color: #374151;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  text-transform: capitalize;
`;

const DragOverlayContainer = styled.div`
  z-index: 1000;
  pointer-events: none;
  
  .block-container {
    transform: rotate(5deg);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    opacity: 0.95;
  }
`;

interface CanvasBlock extends PageBlock {
  block: UIBlock;
}

interface SortableBlockProps {
  canvasBlock: CanvasBlock;
  onEdit: (blockId: string) => void;
  onDelete: (blockId: string) => void;
}

const SortableBlock: React.FC<SortableBlockProps> = ({ canvasBlock, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: canvasBlock.id,
    data: {
      type: 'canvas-block',
      canvasBlock,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <BlockContainer
      ref={setNodeRef}
      style={style}
      isDragging={isDragging}
    >
      <BlockControls className="block-controls">
        <ControlButton
          variant="drag"
          {...attributes}
          {...listeners}
          title="Drag to reorder"
        >
          ⋮⋮
        </ControlButton>
        <ControlButton
          variant="edit"
          onClick={() => onEdit(canvasBlock.id)}
          title="Edit block"
        >
          ✏️
        </ControlButton>
        <ControlButton
          variant="delete"
          onClick={() => onDelete(canvasBlock.id)}
          title="Delete block"
        >
          🗑️
        </ControlButton>
      </BlockControls>

      <BlockHeader>
        <BlockTitle>{canvasBlock.block.name}</BlockTitle>
        <BlockCategory>{canvasBlock.block.category}</BlockCategory>
      </BlockHeader>

      <BlockContent>
        <div className="block-preview">
          {canvasBlock.customHtml || canvasBlock.block.htmlContent}
        </div>
      </BlockContent>
    </BlockContainer>
  );
};

interface PageBuilderCanvasProps {
  pageTitle: string;
  blocks: CanvasBlock[];
  onBlocksChange: (blocks: CanvasBlock[]) => void;
  onBlockEdit: (blockId: string) => void;
  onSave: () => void;
  onPreview: () => void;
  onTitleChange?: (title: string) => void;
  isSaving?: boolean;
  activeId?: string | null;
  activeDragData?: any;
}

export const PageBuilderCanvas: React.FC<PageBuilderCanvasProps> = ({
  pageTitle,
  blocks,
  onBlocksChange,
  onBlockEdit,
  onSave,
  onPreview,
  onTitleChange,
  isSaving = false,
  activeId = null,
  activeDragData = null,
}) => {

  const { isOver, setNodeRef } = useDroppable({
    id: 'canvas-droppable',
    data: {
      type: 'canvas',
    },
  });


  const handleDeleteBlock = (blockId: string) => {
    if (window.confirm('Are you sure you want to delete this block?')) {
      const updatedBlocks = blocks
        .filter(block => block.id !== blockId)
        .map((block, index) => ({ ...block, position: index }));
      onBlocksChange(updatedBlocks);
    }
  };

  // Handle both ui-block (from catalog) and canvas-block (existing blocks) drag types
  const activeBlock = activeId ? blocks.find(block => block.id === activeId) : null;
  const activeUIBlock = activeDragData?.type === 'ui-block' ? activeDragData.block : null;
  const showDragOverlay = activeBlock || activeUIBlock;

  return (
    <>
      <CanvasContainer>
        <CanvasHeader>
          <CanvasTitle 
            value={pageTitle}
            onChange={(e) => onTitleChange?.(e.target.value)}
            placeholder="Enter page title..."
          />
          <CanvasActions>
            <ActionButton onClick={onPreview}>
              Preview
            </ActionButton>
            <ActionButton variant="primary" onClick={onSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Page'}
            </ActionButton>
          </CanvasActions>
        </CanvasHeader>

        <CanvasContent>
          <DroppableArea
            ref={setNodeRef}
            isOver={isOver}
            isEmpty={blocks.length === 0}
          >
            {blocks.length === 0 ? (
              <EmptyStateContent>
                <EmptyStateIcon>🎨</EmptyStateIcon>
                <h3>Start Building Your Page</h3>
                <p>
                  Drag UI blocks from the catalog on the left to start building your landing page.
                  You can reorder blocks by dragging them up or down.
                </p>
              </EmptyStateContent>
            ) : (
              <SortableContext
                items={blocks.map(block => block.id)}
                strategy={verticalListSortingStrategy}
              >
                {blocks.map((canvasBlock) => (
                  <SortableBlock
                    key={canvasBlock.id}
                    canvasBlock={canvasBlock}
                    onEdit={onBlockEdit}
                    onDelete={handleDeleteBlock}
                  />
                ))}
              </SortableContext>
            )}
          </DroppableArea>
        </CanvasContent>
      </CanvasContainer>

      <DragOverlay>
        {showDragOverlay && (
          <DragOverlayContainer>
            <BlockContainer isDragging={false} className="block-container">
              <BlockHeader>
                <BlockTitle>
                  {activeBlock ? activeBlock.block.name : activeUIBlock?.name}
                </BlockTitle>
                <BlockCategory>
                  {activeBlock ? activeBlock.block.category : activeUIBlock?.category}
                </BlockCategory>
              </BlockHeader>
            </BlockContainer>
          </DragOverlayContainer>
        )}
      </DragOverlay>
    </>
  );
};